import {
  AppLayout,
  BreadcrumbGroup,
  BreadcrumbGroupProps,
  Flashbar,
  FlashbarProps,
  SideNavigation,
} from '@cloudscape-design/components';
import { I18nProvider } from '@cloudscape-design/components/i18n';
import messages from '@cloudscape-design/components/i18n/messages/all.en';
import React, { PropsWithChildren } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGlobalSiteSettings } from '../hooks/useGlobalSiteSettings';

const LOCALE = 'en';

type LayoutProps = {
  notifications?: FlashbarProps.MessageDefinition[];
  breadcrumbs?: BreadcrumbGroupProps.Item[];
} & PropsWithChildren;

const Layout = ({
  notifications = [],
  breadcrumbs = [],
  children,
}: LayoutProps) => {
  const mainMenuOpen = useGlobalSiteSettings((state) => state.mainMenuOpen);
  const toggleMainMenuState = useGlobalSiteSettings(
    (state) => state.toggleMainMenuState,
  );
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <I18nProvider locale={LOCALE} messages={[messages]}>
      <AppLayout
        breadcrumbs={
          <BreadcrumbGroup
            onFollow={(event) => {
              if (!event.detail.external) {
                event.preventDefault();
                navigate(event.detail.href);
              }
            }}
            items={breadcrumbs}
          />
        }
        navigationOpen={mainMenuOpen}
        onNavigationChange={toggleMainMenuState}
        navigation={
          <SideNavigation
            activeHref={location.pathname}
            onFollow={(event) => {
              event.preventDefault();
              navigate(event.detail.href);
            }}
            header={{
              href: '/userinformations',
              text: "Theo's Bistro ",
            }}
            items={[
              {
                href: '/userinformations',
                text: 'Besucherinformationen',
                type: 'link',
              },
              {
                href: '/website',
                text: 'Webseite',
                type: 'expandable-link-group',
                items: [
                  {
                    href: '/website/weeklymenue',
                    text: 'Wochenkarte',
                    type: 'expandable-link-group',
                    items: [
                      {
                        href: '/website/weeklymenue/menues/1',
                        text: 'Woche 1',
                        type: 'link',
                      },
                      {
                        href: '/website/weeklymenue/menues/2',
                        text: 'Woche 2',
                        type: 'link',
                      },
                      {
                        href: '/website/weeklymenue/menues/3',
                        text: 'Woche 3',
                        type: 'link',
                      },
                    ],
                  },
                  {
                    href: '/website/food',
                    text: 'Speisekarte',
                    type: 'expandable-link-group',
                    items: [
                      {
                        href: '/website/food/foodcategories',
                        text: 'Kategorien',
                        type: 'link',
                      },
                      {
                        href: '/website/food/menues',
                        text: 'Speisen',
                        type: 'link',
                      },
                      {
                        href: '/website/food/allergens',
                        text: 'Allergene',
                        type: 'link',
                      },
                      {
                        href: '/website/food/additives',
                        text: 'Zusatzstoffe',
                        type: 'link',
                      },
                    ],
                  },
                  {
                    href: '/website/gallery',
                    text: 'Galerie',
                    type: 'link',
                  },
                  {
                    href: '/website/news',
                    text: 'Aktuelles',
                    type: 'expandable-link-group',
                    items: [
                      {
                        href: '/website/news/articles',
                        text: 'Artikel',
                        type: 'link',
                      },
                    ],
                  },
                  {
                    href: '/website/events',
                    text: 'Events',
                    type: 'expandable-link-group',
                    items: [
                      {
                        href: '/website/events/evententries',
                        text: 'Einträge',
                        type: 'link',
                      },
                    ],
                  },
                  {
                    href: '/website/about',
                    text: "Über Theo's",
                    type: 'link',
                  },
                  {
                    href: '/website/partners',
                    text: 'Partner',
                    type: 'link',
                  },
                  {
                    href: '/website/legal',
                    text: 'Rechtliches',
                    type: 'link',
                  },
                ],
              },
              {
                type: 'divider',
              },
              {
                href: '/logout',
                text: 'Logout',
                type: 'link',
              },
            ]}
          />
        }
        notifications={<Flashbar items={notifications} />}
        toolsHide
        content={children}
      />
    </I18nProvider>
  );
};

export default Layout;
